/**
 * Wishlist / Like Items.
 *
 * Persistence model:
 * - Guests: localStorage only, under WishlistStore.GUEST_KEY. Cleared/ignored once a customer logs in
 *   (guest-picked items are not silently attributed to whichever account logs in next).
 * - Logged-in customers: source of truth is the store's wishlist app proxy at /apps/wishlist (see
 *   WishlistStore.ENDPOINT below) backed by a customer metafield or equivalent server-side record, so
 *   the list persists across sessions/browsers/devices. Storefront themes cannot write customer
 *   metafields directly (Admin API only) - this proxy is the small backend piece required to fulfil
 *   the "wishlist persists for signed-in customers" requirement; until it is deployed, calls fail soft
 *   and fall back to a per-customer localStorage cache (still keyed by customer id, never shared with
 *   the guest list) so the UI keeps working in the meantime.
 *
 * Each entry is stored as { id, handle } so the wishlist page can render product data via Shopify's
 * public /products/{handle}.js AJAX endpoint without needing a Storefront API token.
 */
class WishlistStore extends EventTarget {
  static ENDPOINT = '/apps/wishlist';
  static GUEST_KEY = 'ambar:wishlist:guest';

  constructor() {
    super();
    this.customerId = document.body.getAttribute('data-customer-id') || null;
    this.items = new Map();
    this.ready = this._load();
  }

  get storageKey() {
    return this.customerId ? `ambar:wishlist:customer:${this.customerId}` : WishlistStore.GUEST_KEY;
  }

  _readLocal() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  _writeLocal() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify([...this.items.values()]));
    } catch (e) {
      /* storage unavailable (private mode/quota) - in-memory list still works for this page view */
    }
  }

  async _load() {
    this.items = new Map(this._readLocal().map((item) => [String(item.id), item]));

    if (this.customerId) {
      try {
        const res = await fetch(`${WishlistStore.ENDPOINT}?customer_id=${this.customerId}`, {
          headers: { Accept: 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          this.items = new Map((data.items || []).map((item) => [String(item.id), item]));
          this._writeLocal();
        }
      } catch (e) {
        /* proxy not deployed yet or offline - keep the local cache as a fallback */
      }
    }

    this.dispatchEvent(new CustomEvent('sync'));
    return this.items;
  }

  has(productId) {
    return this.items.has(String(productId));
  }

  getAll() {
    return [...this.items.values()];
  }

  async toggle(productId, handle) {
    productId = String(productId);
    const nowLiked = !this.has(productId);

    if (nowLiked) this.items.set(productId, { id: productId, handle });
    else this.items.delete(productId);
    this._writeLocal();
    this.dispatchEvent(new CustomEvent('change', { detail: { productId, liked: nowLiked } }));

    if (this.customerId) {
      try {
        await fetch(WishlistStore.ENDPOINT, {
          method: nowLiked ? 'POST' : 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_id: this.customerId, product_id: productId, handle }),
        });
      } catch (e) {
        /* optimistic update stands locally; will reconcile next successful sync */
      }
    }

    return nowLiked;
  }
}

window.ambarWishlist = window.ambarWishlist || new WishlistStore();

class WishlistButton extends HTMLElement {
  static PROMPT_KEY = 'ambar:wishlist:prompted';

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.handle = this.dataset.productHandle;
    this.loggedIn = this.dataset.loggedIn === 'true';
    this.button = this.querySelector('.wishlist-button__toggle');

    this._onClick = this._onClick.bind(this);
    this._onSync = this._render.bind(this);

    this.button.addEventListener('click', this._onClick);
    window.ambarWishlist.addEventListener('sync', this._onSync);
    window.ambarWishlist.addEventListener('change', this._onSync);
    window.ambarWishlist.ready.then(() => this._render());
  }

  disconnectedCallback() {
    window.ambarWishlist.removeEventListener('sync', this._onSync);
    window.ambarWishlist.removeEventListener('change', this._onSync);
  }

  _render() {
    const liked = window.ambarWishlist.has(this.productId);
    this.button.setAttribute('aria-pressed', String(liked));
    this.button.classList.toggle('is-liked', liked);
  }

  _onClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.button.disabled = true;
    window.ambarWishlist
      .toggle(this.productId, this.handle)
      .then((liked) => {
        // Guests keep their list in localStorage (see WishlistStore.GUEST_KEY), so the
        // heart always works. The account modal is now a prompt, not a gate: shown once
        // per session on the first like, to explain how to keep the list across devices.
        if (liked) this._maybePromptForAccount();
      })
      .finally(() => {
        this.button.disabled = false;
      });
  }

  _maybePromptForAccount() {
    if (this.loggedIn) return;
    try {
      if (sessionStorage.getItem(WishlistButton.PROMPT_KEY)) return;
      sessionStorage.setItem(WishlistButton.PROMPT_KEY, '1');
    } catch (e) {
      /* storage unavailable (private mode) - prompt once per page view instead */
    }
    const modal = document.getElementById('WishlistAccountModal');
    if (modal && typeof modal.show === 'function') modal.show(this.button);
  }
}

customElements.define('wishlist-button', WishlistButton);

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    // Section HTML keyed by handle. A like/unlike anywhere fires `change`, which
    // re-renders the whole grid; without this every removal would refetch every
    // remaining card.
    this.cache = new Map();
    window.ambarWishlist.ready.then(() => this._render());
    window.ambarWishlist.addEventListener('change', () => this._render());
  }

  get rootUrl() {
    // '/', '/en/', '/fr/' ... - always trailing-slashed by Shopify.
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async _render() {
    const items = window.ambarWishlist.getAll();

    if (!items.length) {
      this.grid.innerHTML = '';
      this.emptyState.hidden = false;
      return;
    }
    this.emptyState.hidden = true;

    // The card markup is the theme's own product-card snippet, rendered by
    // sections/wishlist-card.liquid through the Section Rendering API. Building
    // it here in JS instead would mean maintaining a second card that silently
    // drifts from the real one every time a theme setting changes.
    const cards = await Promise.all(items.map((item) => this._fetchCard(item)));

    const parser = new DOMParser();
    this.grid.innerHTML = '';

    cards.forEach(({ item, html }) => {
      let node = null;
      if (html) {
        const doc = parser.parseFromString(html, 'text/html');
        node = doc.querySelector('[data-wishlist-card]');
      }
      this.grid.appendChild(node ? document.importNode(node, true) : this._unavailableCard(item));
    });

    this.grid.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        window.ambarWishlist.toggle(btn.dataset.wishlistRemove, btn.dataset.wishlistHandle);
      });
    });

    // The cards carry the same hover slider as every other grid on the site.
    if (typeof window.initProductCardSliders === 'function') window.initProductCardSliders();
  }

  async _fetchCard(item) {
    if (this.cache.has(item.handle)) return { item, html: this.cache.get(item.handle) };
    try {
      const res = await fetch(`${this.rootUrl}products/${item.handle}?section_id=wishlist-card`);
      if (!res.ok) return { item, html: null };
      const html = await res.text();
      this.cache.set(item.handle, html);
      return { item, html };
    } catch (e) {
      // Deleted or unpublished product - fall through to the placeholder card.
      return { item, html: null };
    }
  }

  _unavailableCard(item) {
    const li = document.createElement('li');
    li.className = 'collection-product-card wishlist-page__card wishlist-page__card--unavailable';
    li.innerHTML = `
      <p class="wishlist-page__item-status wishlist-page__item-status--unavailable">${this.dataset.labelUnavailable}</p>
      <button type="button" class="wishlist-page__remove" data-wishlist-remove="${item.id}" data-wishlist-handle="${item.handle}">${this.dataset.labelRemove}</button>`;
    return li;
  }
}

customElements.define('wishlist-page', WishlistPage);
